/**
 * AwardCelebration — a home-page banner + confetti the first time a user lands
 * after HR released a Drivers award to them (Sep 2026). Checks the cabinet at
 * most once per browser session (the callable can cold-start, and awards are
 * released monthly), only for users provisioned for Drivers.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import type { CabinetStar } from '@daaily/shared';
import { Icon } from '../design/Icon';
import { useProfile } from '../profile/ProfileProvider';
import { callDriversMyCabinet } from './lib/callables';
import { fireConfetti } from '../components/confetti';
import { markCelebrated, uncelebratedStars } from './celebrate';
import { Medal } from './Trophy';
import { monthLabel } from './lib/format';

const SESSION_KEY = 'daaily.driversCelebrationChecked';

export function AwardCelebration() {
  const { state } = useProfile();
  const enabled = state.status === 'ready' && state.user.tools?.drivers?.enabled === true;
  const email = state.status === 'ready' ? state.user.email : '';
  const [fresh, setFresh] = useState<CabinetStar[]>([]);

  useEffect(() => {
    if (!enabled) return;
    const flag = `${SESSION_KEY}:${email.toLowerCase()}`;
    try { if (sessionStorage.getItem(flag)) return; } catch { /* ignore */ }
    let alive = true;
    callDriversMyCabinet()
      .then((r) => {
        // Flag only after a successful check, so a failed call retries next landing.
        try { sessionStorage.setItem(flag, '1'); } catch { /* ignore */ }
        const stars = uncelebratedStars(email, r.data.cabinet.stars);
        if (!alive || stars.length === 0) return;
        setFresh(stars);
        markCelebrated(email, stars);
        window.setTimeout(() => fireConfetti(), 300);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [enabled, email]);

  if (fresh.length === 0) return null;
  const first = fresh[0]!;
  const names = [...new Set(fresh.map((s) => s.valueName))].join(' and ');
  return (
    <section className="relative flex items-center gap-4 rounded-lg border border-line bg-surface p-4 pr-10" aria-live="polite">
      <Medal valueId={first.valueId} size={56} label={`${first.valueName} award`} />
      <div className="min-w-0">
        <p className="text-base font-semibold text-fg">You won the {names} award!</p>
        <p className="text-sm text-fg-muted">
          Your colleagues nominated you and HR picked you as a DAAily Driver for {monthLabel(first.month)}.{' '}
          <Link to="/drivers" className="text-brand underline underline-offset-2">See your trophy cabinet</Link>
        </p>
      </div>
      <button
        type="button"
        onClick={() => setFresh([])}
        aria-label="Dismiss"
        className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2"
      >
        <Icon as={X} />
      </button>
    </section>
  );
}
